// src/services/api.ts

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { API_CONFIG } from '../config/api.config';
import type { ApiResponse, ApiError } from '../types/api.types';

/**
 * Main API Service for AVEC Admin Dashboard
 * Handles HTTP requests with authentication, error handling, and response transformation
 */

class ApiService {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token and multi-tenant filtering
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        
        // Don't add headers for OPTIONS requests
        if (config.method?.toLowerCase() === 'options') {
          return config;
        }
        
        // Set default headers if not already set
        config.headers = config.headers || {};
        
        // Add auth token if available
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add required headers
        // config.headers.tenant = API_CONFIG.DEFAULT_TENANT;
        config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
        config.headers.Accept = config.headers.Accept || 'application/json';
        
        // Add multi-tenant filtering
        config = this.addMultiTenantFiltering(config);
        
        console.log('Envoi de la requête vers:', config.url);
        return config;
      },
      (error) => {
        console.error('Erreur dans l\'intercepteur de requête:', error);
        return Promise.reject(this.transformError(error));
      }
    );

    // Response interceptor - Handle responses and errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        console.log('API Response:', {
          url: response.config.url,
          status: response.status,
          data: response.data,
          headers: response.headers
        });
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            console.log('Token expiré, tentative de rafraîchissement...');
            const newToken = await this.refreshToken();
            
            if (newToken) {
              console.log('Token rafraîchi avec succès');
              
              // Process all queued requests
              this.processQueue(null, newToken);
              
              // Retry the original request
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.axiosInstance(originalRequest);
            } else {
              throw new Error('Impossible de rafraîchir le token');
            }
          } catch (refreshError) {
            console.error('Échec du rafraîchissement du token:', refreshError);
            
            // Process queue with error
            this.processQueue(refreshError, null);
            
            // Handle auth failure
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(this.transformError(error));
      }
    );
  }

  /**
   * Process queued requests after token refresh
   */
  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  /**
   * Get stored authentication token
   */
  private getStoredToken(): string | null {
    try {
      const authData = localStorage.getItem('avec_auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.tokens?.accessToken || null;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du token:', error);
    }
    return null;
  }

  /**
   * Get stored refresh token
   */
  private getStoredRefreshToken(): string | null {
    try {
      const authData = localStorage.getItem('avec_auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.tokens?.refreshToken || null;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du refresh token:', error);
    }
    return null;
  }

  /**
   * Get stored user data for multi-tenant filtering
   */
  private getStoredUser(): any {
    try {
      const authData = localStorage.getItem('avec_auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user || null;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données utilisateur:', error);
    }
    return null;
  }

  /**
   * Add multi-tenant filtering to requests based on user role (5-level hierarchy)
   * Hierarchy: regions > provinces > communes > villages > groups
   */
  private addMultiTenantFiltering(config: any): any {
    const user = this.getStoredUser();
    if (!user) return config;

    const url = config.url;
    const method = config.method?.toLowerCase();
    
    // Skip filtering for auth endpoints
    if (url?.includes('/auth/')) {
      return config;
    }

    // Get user role and assigned IDs for all hierarchy levels
    const userRole = user?.Staff?.role || user?.profile?.role || user?.staffRole;
    const assignedRegionId = user?.Staff?.assigned_region_id || 
                            user?.profile?.assigned_region_id || 
                            user?.assignedRegionId;
    const assignedProvinceId = user?.Staff?.assigned_province_id || 
                              user?.profile?.assigned_province_id || 
                              user?.assignedProvinceId;
    const assignedCommuneId = user?.Staff?.assigned_commune_id || 
                             user?.profile?.assigned_commune_id || 
                             user?.assignedCommuneId;
    const assignedVillageId = user?.Staff?.assigned_village_id || 
                             user?.profile?.assigned_village_id || 
                             user?.assignedVillageId;
    const assignedGroupId = user?.Staff?.assigned_group_id || 
                           user?.profile?.assigned_group_id || 
                           user?.assignedGroupId;

    // Super admins get no filtering - they see everything
    if (userRole === 'super_admin' || userRole === 'administrator') {
      return config;
    }

    // Initialize params if not exist
    if (!config.params) {
      config.params = {};
    }

    // Apply filtering based on user role and hierarchy level
    if (userRole === 'regional_admin' && assignedRegionId) {
      // Regional admins only see data from their assigned region
      if (this.shouldFilterByRegionId(url)) {
        config.params.regionId = assignedRegionId;
      }
    } else if (userRole === 'provincial_admin' && assignedProvinceId) {
      // Provincial admins only see data from their assigned province
      if (this.shouldFilterByProvinceId(url)) {
        config.params.provinceId = assignedProvinceId;
      }
    } else if (userRole === 'communal_admin' && assignedCommuneId) {
      // Communal admins only see data from their assigned commune
      if (this.shouldFilterByCommuneId(url)) {
        config.params.communeId = assignedCommuneId;
      }
    } else if (userRole === 'village_admin' && assignedVillageId) {
      // Village admins only see data from their assigned village
      if (this.shouldFilterByVillageId(url)) {
        config.params.villageId = assignedVillageId;
      }
    } else if (userRole === 'group_agent' && assignedGroupId) {
      // Group agents only see data from their assigned group
      if (this.shouldFilterByGroupId(url)) {
        config.params.groupId = assignedGroupId;
      }
    }

    return config;
  }

  /**
   * Check if endpoint should be filtered by region ID
   */
  private shouldFilterByRegionId(url?: string): boolean {
    if (!url) return false;
    
    // Don't filter if URL already contains specific route patterns
    if (url.includes('/regions/') || url.includes('/auth/') || url.includes('/settings/')) {
      return false;
    }
    
    const regionFilterEndpoints = [
      '/provinces',
      '/communes', 
      '/villages',
      '/groups',
      '/members',
      '/savings',
      '/credits',
      '/meetings',
      '/reports',
      '/staff',
      '/solidarity',
      '/payments',
      '/activities',
      '/expenses',
      '/contributions',
      '/weekly-contributions',
      '/solidarity-contributions'
    ];

    return regionFilterEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if endpoint should be filtered by province ID
   */
  private shouldFilterByProvinceId(url?: string): boolean {
    if (!url) return false;
    
    // Don't filter if URL already contains specific route patterns
    if (url.includes('/provinces/') || url.includes('/auth/') || url.includes('/settings/')) {
      return false;
    }
    
    const provinceFilterEndpoints = [
      '/communes',
      '/villages', 
      '/groups',
      '/members',
      '/savings',
      '/credits',
      '/meetings',
      '/reports',
      '/staff',
      '/solidarity',
      '/payments',
      '/activities',
      '/expenses',
      '/contributions',
      '/weekly-contributions',
      '/solidarity-contributions'
    ];

    return provinceFilterEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if endpoint should be filtered by village ID
   */
  private shouldFilterByVillageId(url?: string): boolean {
    if (!url) return false;
    
    // Don't filter if URL already contains specific route patterns
    if (url.includes('/villages/') || url.includes('/auth/') || url.includes('/settings/')) {
      return false;
    }
    
    // Don't filter if URL is a group-specific endpoint
    if (url.match(/\/groups\/[^\/]+\//)) {
      return false;
    }
    
    const villageFilterEndpoints = [
      '/groups',
      '/members',
      '/savings', 
      '/credits',
      '/meetings',
      '/solidarity',
      '/activities',
      '/expenses',
      '/contributions',
      '/weekly-contributions',
      '/solidarity-contributions'
    ];

    return villageFilterEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if endpoint should be filtered by commune ID
   */
  private shouldFilterByCommuneId(url?: string): boolean {
    if (!url) return false;
    
    // Don't filter if URL already contains specific route patterns
    if (url.includes('/communes/') || url.includes('/auth/') || url.includes('/settings/')) {
      return false;
    }
    
    // Don't filter if URL is a group-specific endpoint (e.g., /groups/{id}/expenses)
    if (url.match(/\/groups\/[^\/]+\//)) {
      return false;
    }
    
    // Don't filter action endpoints (e.g., /contributions/{id}/record, /savings/contributions/{id}/record)
    if (url.match(/\/(savings\/)?contributions\/[^\/]+\/(record|mark-missed|waive|generate)/)) {
      return false;
    }
    
    // Don't filter solidarity contribution action endpoints
    if (url.match(/\/solidarity-contributions\/[^\/]+\/(record|mark-missed|waive)/)) {
      return false;
    }
    
    const communeFilterEndpoints = [
      '/members',
      '/savings',
      '/credits', 
      '/groups',
      '/meetings',
      '/reports',
      '/staff',
      '/solidarity',
      '/payments',
      '/activities',
      '/expenses',
      '/contributions',
      '/weekly-contributions',
      '/solidarity-contributions'
    ];

    return communeFilterEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if endpoint should be filtered by group ID  
   */
  private shouldFilterByGroupId(url?: string): boolean {
    if (!url) return false;
    
    // Don't filter if URL already contains specific route patterns
    if (url.includes('/groups/') || url.includes('/communes/') || url.includes('/auth/') || url.includes('/settings/')) {
      return false;
    }
    
    // Don't filter if URL is a group-specific endpoint (e.g., /groups/{id}/expenses)
    if (url.match(/\/groups\/[^\/]+\//)) {
      return false;
    }
    
    // Don't filter action endpoints (e.g., /contributions/{id}/record, /savings/contributions/{id}/record)
    if (url.match(/\/(savings\/)?contributions\/[^\/]+\/(record|mark-missed|waive|generate)/)) {
      return false;
    }
    
    // Don't filter solidarity contribution action endpoints
    if (url.match(/\/solidarity-contributions\/[^\/]+\/(record|mark-missed|waive)/)) {
      return false;
    }
    
    const groupFilterEndpoints = [
      '/members',
      '/savings', 
      '/credits',
      '/meetings',
      '/solidarity',
      '/activities',
      '/expenses',
      '/contributions',
      '/weekly-contributions',
      '/solidarity-contributions'
    ];

    return groupFilterEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Refresh authentication token using the new API format
   */
  private async refreshToken(): Promise<string | null> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      console.warn('Aucun refresh token disponible');
      throw new Error('Aucun refresh token disponible');
    }

    try {
      console.log('Tentative de rafraîchissement du token...');
      
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.REFRESH}`,
        { refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
            // 'tenant': API_CONFIG.DEFAULT_TENANT,
          }
        }
      );

      console.log('Réponse du rafraîchissement:', response.data);

      if (response.data.success && response.data.data?.accessToken) {
        const newAccessToken = response.data.data.accessToken;
        
        // Update stored auth data with new token
        const authData = JSON.parse(localStorage.getItem('avec_auth') || '{}');
        if (authData.tokens) {
          authData.tokens.accessToken = newAccessToken;
          // Keep the same refresh token unless a new one is provided
          if (response.data.data.refreshToken) {
            authData.tokens.refreshToken = response.data.data.refreshToken;
          }
          localStorage.setItem('avec_auth', JSON.stringify(authData));
          
          console.log('Token mis à jour dans le stockage local');
          
          // Trigger token refresh event
          window.dispatchEvent(new CustomEvent('auth:token-refreshed'));
          
          return newAccessToken;
        }
      }

      throw new Error('Format de réponse invalide pour le rafraîchissement du token');
    } catch (error: any) {
      console.error('Erreur lors du rafraîchissement du token:', error);
      
      // If refresh fails, clear stored auth data
      localStorage.removeItem('avec_auth');
      
      // Re-throw for handling by caller
      throw error;
    }
  }

  /**
   * Handle authentication failure
   */
  private handleAuthFailure(): void {
    console.log('Échec de l\'authentification, nettoyage...');
    localStorage.removeItem('avec_auth');
    
    // Trigger auth context update
    window.dispatchEvent(new CustomEvent('auth:logout'));
    
    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
  }

  /**
   * Transform axios error to our standard error format
   */
  private transformError(error: any): ApiError {
    if (error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || 'Une erreur s\'est produite',
        error: {
          code: error.response.status?.toString() || 'UNKNOWN',
          details: error.response.data,
        },
        timestamp: new Date().toISOString(),
      };
    }

    if (error.request) {
      return {
        success: false,
        message: 'Erreur réseau. Veuillez vérifier votre connexion.',
        error: {
          code: 'NETWORK_ERROR',
        },
        timestamp: new Date().toISOString(),
      };
    }

    return {
      success: false,
      message: error.message || 'Une erreur inattendue s\'est produite',
      error: {
        code: 'UNKNOWN_ERROR',
      },
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Generic GET request
   */
  async get<T = any>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get<ApiResponse<T>>(endpoint, config);
    return response.data;
  }

  /**
   * Generic POST request
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post<ApiResponse<T>>(endpoint, data, config);
    return response.data;
  }

  /**
   * Generic PUT request
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put<ApiResponse<T>>(endpoint, data, config);
    return response.data;
  }

  /**
   * Generic PATCH request
   */
  async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.patch<ApiResponse<T>>(endpoint, data, config);
    return response.data;
  }

  /**
   * Generic DELETE request
   */
  async delete<T = any>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete<ApiResponse<T>>(endpoint, config);
    return response.data;
  }

  /**
   * Upload file
   */
  async upload<T = any>(
    endpoint: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.axiosInstance.post<ApiResponse<T>>(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }
    );
    
    return response.data;
  }

  /**
   * Get axios instance for custom requests
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  /**
   * Manual token refresh for testing
   */
  async manualRefreshToken(): Promise<boolean> {
    try {
      const newToken = await this.refreshToken();
      return !!newToken;
    } catch (error) {
      console.error('Échec du rafraîchissement manuel:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated and token is valid
   */
  isAuthenticated(): boolean {
    const token = this.getStoredToken();
    const refreshToken = this.getStoredRefreshToken();
    return !!(token && refreshToken);
  }

  /**
   * Clear authentication data
   */
  clearAuth(): void {
    localStorage.removeItem('avec_auth');
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;